/**
 * Product quiz recommendation engine.
 *
 * PURE MODULE — no DOM, no network, no analytics, no globals read at runtime.
 * Everything it needs is injected. This is deliberate: the engine must be
 * replayable over archived answers so that TIER_WEIGHT can be recalibrated
 * against real conversion data and the before/after recommendations compared.
 *
 * Reference: brief "Algorithme de recommandation du product quiz" (2026-09-11).
 * Section numbers in the comments below point back at that brief.
 *
 * Loaded both as a plain browser script (assigns globalThis.TwtQuizRecommend)
 * and as an ES module in Node for the unit tests.
 */
(function (root) {
  'use strict';

  /**
   * Bump on ANY change that can alter which board comes out for the same
   * answers — weights, filters, slot rules, the activity mapping. Persisted
   * alongside the answers so a replay knows which version produced a given
   * historical recommendation, and so an A/B of two versions is legible.
   */
  const ENGINE_VERSION = '1.1.0';

  // --- Taxonomies (§2) — closed lists, mirrored from the Shopify metafield
  // choice-list validations. Nothing outside these values can be stored.

  const ARCHETYPES = [
    'The Tribe Captain',
    'The Explorer',
    'The Cruiser',
    'The Voyager',
    'The Performer',
    'The Angler',
    'The River Runner',
    'The Freestyler',
  ];

  const LEVELS = ['Beginner friendly', 'Confident paddler', 'Performance ready'];

  const ACTIVITIES = [
    'Cruising',
    'Touring',
    'Ocean & Coastal',
    'Fitness & Yoga',
    'Pets',
    'Kids & Family',
    'Camping & Cargo',
    'Fishing',
    'Surf',
    'Whitewater',
    'Kayak mode',
  ];

  // Stability -> glide scale for the 5 generalists (§2). Specialists are off
  // the scale and use SPECIALIST_ADJACENCY instead.
  const GLIDE_SCALE = [
    'The Tribe Captain',
    'The Explorer',
    'The Cruiser',
    'The Voyager',
    'The Performer',
  ];

  const SPECIALIST_ADJACENCY = {
    'The Angler': 'The Tribe Captain',
    'The River Runner': 'The Explorer',
    'The Freestyler': 'The Cruiser',
  };

  /**
   * Commercial preference applied to the fit score (§6).
   *
   * THE TUNING LEVER — exported, defined in exactly one place. Revisit after
   * 2-4 weeks of conversion data broken down by quiz_primary_board (§11);
   * `scripts/replay-quiz.mjs --tier-weight` diffs a candidate against history.
   *
   * Know which knob does what. §6 calls `budget: -30` "the rule that solves the
   * cannibalisation problem", but it is not — and in fact `budget` cannot change
   * any outcome at all. Budget boards are never compared against non-budget ones
   * in any ranking: slot 1 and slot 2 draw from a pool that already excludes
   * them, slot 3's pool is entirely budget (so a constant shift is a no-op), and
   * the last-resort fallback only runs when the non-budget pool is empty. What
   * actually keeps the Nomad out of slot 1 is the POOL FILTER `tier != budget`.
   *
   * Verified over 198 replayed answer sets: every value of `budget` from -30 to
   * 0 produces byte-identical recommendations. `core` and `premium` are the real
   * levers — moving `premium` to 25 changes 35% of slot-1 picks.
   */
  const TIER_WEIGHT = {
    budget: -30,
    core: 10,
    premium: 10,
    specialist: 0,
  };

  // Always use the UPPER bound of the band (§4): we filter on the worst case of
  // the range, so a 299 lb paddler never gets a board rated for 250.
  const LOAD_LB = {
    lt160: 160,
    '160_200': 200,
    '201_250': 250,
    '251_300': 300,
    gt300: 340,
  };

  // Deliberate margin (§2, filter 1): a board technically under its stated
  // limit is mediocre in real use.
  const CAPACITY_MARGIN = 0.85;

  // Above this load ratio the card carries a warning (§8).
  const LOAD_WARNING_RATIO = 0.75;

  const SLOT_LABELS = {
    best: 'Best choice',
    more_stable: 'More stable',
    faster: 'Faster',
    budget: 'Budget option',
  };

  // Display copy keyed by archetype. Kept here (not in the UI) so a replay of
  // archived answers reproduces the exact headline that was shown.
  const ARCHETYPE_COPY = {
    'The Tribe Captain': { headline: 'You are the Tribe Captain' },
    'The Explorer': { headline: 'You are an Explorer' },
    'The Cruiser': { headline: 'You are a Cruiser' },
    'The Voyager': { headline: 'You are a long-distance Voyager' },
    'The Performer': { headline: 'You are a Performer' },
    'The Angler': { headline: 'You are an Angler' },
    'The River Runner': { headline: 'You are a River Runner' },
    'The Freestyler': { headline: 'You are a Freestyler' },
  };

  // --- Answer codec ---------------------------------------------------------
  //
  // §12.2: without stored raw answers, recalibrating TIER_WEIGHT is impossible —
  // you cannot replay history. These two functions are the storage format, and
  // they must round-trip EXACTLY: a replay that decodes to something other than
  // what the customer actually answered produces a confident wrong conclusion.
  //
  // Kept deliberately human-readable rather than compact, because the primary
  // place these strings are read is the Shopify order admin, by a person.

  /** The §4 contract. Decoding validates against it and refuses anything else. */
  const ANSWER_VALUES = {
    water: ['calm_lake', 'big_lake_wind', 'river_coast', 'anywhere'],
    use_case: ['relax', 'distance', 'fitness', 'family_cargo', 'fishing', 'whitewater_surf'],
    load_type: ['solo', 'solo_gear', 'small_companion', 'large_companion'],
    total_load: ['lt160', '160_200', '201_250', '251_300', 'gt300'],
    priority: ['max_stability', 'balanced', 'speed', 'maneuverable'],
    experience: ['never', 'comfortable_wide', 'regular', 'performance'],
    accessory_interests: [
      'easy_inflation',
      'phone_dry',
      'food_drinks',
      'kayak',
      'transport',
      'stay_put',
      'safety',
      'none',
    ],
  };

  const ANSWER_KEYS = Object.keys(ANSWER_VALUES);

  /**
   * Answers -> "water=calm_lake|use_case=relax|...".
   *
   * Only the seven quiz keys are ever encoded. `email` and anything else on the
   * answers object is dropped on purpose: this string is written to a cart
   * attribute that a merchant reads in the order admin, and the order already
   * carries the customer's email through its own channel.
   */
  function encodeAnswers(answers) {
    const safe = answers || {};
    const parts = [];
    ANSWER_KEYS.forEach((key) => {
      const value = safe[key];
      if (value == null) return;
      if (key === 'accessory_interests') {
        const list = (Array.isArray(value) ? value : [value]).filter(
          (entry) => ANSWER_VALUES[key].indexOf(entry) !== -1
        );
        if (list.length) parts.push(key + '=' + list.join(','));
        return;
      }
      if (ANSWER_VALUES[key].indexOf(value) !== -1) parts.push(key + '=' + value);
    });
    return parts.join('|');
  }

  /**
   * Inverse of encodeAnswers. Returns null if ANYTHING is unrecognised rather
   * than salvaging what it can — a partially-decoded answer set would silently
   * skew a calibration run, which is worse than dropping the row.
   */
  function decodeAnswers(encoded) {
    if (typeof encoded !== 'string' || !encoded.length) return null;
    const answers = {};
    const pairs = encoded.split('|');
    for (let index = 0; index < pairs.length; index += 1) {
      const separator = pairs[index].indexOf('=');
      if (separator === -1) return null;
      const key = pairs[index].slice(0, separator);
      const raw = pairs[index].slice(separator + 1);
      const allowed = ANSWER_VALUES[key];
      if (!allowed) return null;
      if (Object.prototype.hasOwnProperty.call(answers, key)) return null;
      if (key === 'accessory_interests') {
        const list = raw.split(',');
        if (list.some((entry) => allowed.indexOf(entry) === -1)) return null;
        answers[key] = list;
      } else {
        if (allowed.indexOf(raw) === -1) return null;
        answers[key] = raw;
      }
    }
    return answers;
  }

  // --- Spec normalisation --------------------------------------------------
  //
  // §2 calls paddleboard_width / _weight_limit / _length "pre-existing and
  // reliable", and they are — but they are single-line TEXT, not numbers, and
  // the merchant writes them freehand: "350 lbs", "300lbs", "425lbs", '33"',
  // "11'6\"", "12'6", "9'6'". Doing arithmetic on those strings yields NaN, and
  // every NaN comparison is false — which would silently switch OFF the
  // capacity filter and hand a 300 lb paddler a 250 lb board. Parse first.

  /** First number in a free-text measure. '33"' -> 33, "300lbs" -> 300. */
  function parseMeasure(raw) {
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    if (raw && typeof raw === 'object' && typeof raw.value === 'number') return raw.value;
    if (typeof raw !== 'string') return null;
    const match = raw.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  /** Feet/inches to inches. "11'6\"" -> 138, "12'6" -> 150, "11'" -> 132. */
  function parseLength(raw) {
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    if (typeof raw !== 'string') return null;
    const match = raw.match(/(\d+)\s*'\s*(\d+)?/);
    if (!match) return parseMeasure(raw);
    return parseInt(match[1], 10) * 12 + (match[2] ? parseInt(match[2], 10) : 0);
  }

  function normalizeBoard(board) {
    return Object.assign({}, board, {
      width_in: parseMeasure(board.width_in),
      capacity_lb: parseMeasure(board.capacity_lb),
      length_in: parseLength(board.length_in),
      avg_weekly_sales: parseMeasure(board.avg_weekly_sales) || 0,
      price_cad: parseMeasure(board.price_cad),
      price_tier: board.price_tier || derivePriceTier(board),
    });
  }

  /**
   * STOPGAP — §2 deploys profile_archetype / profile_level / activities but no
   * `custom.price_tier`, and §6 scores on one. Until that metafield exists this
   * derives it deterministically so the value is reproducible on replay.
   *
   * Lives here rather than in the UI layer precisely because it feeds the fit
   * score: a tier derived in view code would not be replayable.
   */
  const PRICE_TIER_BY_HANDLE = {
    'nomad-11-entry-level-waka-inflatable-paddleboard-with-enhanced-speed': 'budget',
    'nomad-xl-11-entry-level-inflatable-paddleboard-with-enhanced-stability': 'budget',
  };

  const PREMIUM_PRICE_FLOOR_CAD = 1000;

  function derivePriceTier(board) {
    if (board.price_tier) return board.price_tier;
    if (PRICE_TIER_BY_HANDLE[board.handle]) return PRICE_TIER_BY_HANDLE[board.handle];
    const primary = (board.archetypes || [])[0];
    if (SPECIALIST_ADJACENCY[primary]) return 'specialist';
    return board.price_cad >= PREMIUM_PRICE_FLOOR_CAD ? 'premium' : 'core';
  }

  // --- Step 1: paddler level (§5) -----------------------------------------

  function resolveLevel(answers) {
    let level;
    if (answers.experience === 'never') level = 'Beginner friendly';
    else if (answers.experience === 'performance') level = 'Performance ready';
    else if (answers.experience === 'comfortable_wide' || answers.experience === 'regular') {
      level = 'Confident paddler';
    } else level = 'Beginner friendly';

    // Override: declared fear beats declared experience.
    if (answers.priority === 'max_stability') level = 'Beginner friendly';

    return level;
  }

  // --- Steps 3-5: archetype (§5) ------------------------------------------

  // Step 5 weights. Only the answer values that can still reach step 5 are
  // listed: family_cargo / large_companion / loads >= 251 are intercepted by
  // the Captain trigger, and fishing / whitewater_surf by the specialist
  // override, so they never contribute an index term.
  const GLIDE_WEIGHTS = {
    water: { calm_lake: 5, big_lake_wind: -5, river_coast: -5, anywhere: 0 },
    use_case: { relax: 0, distance: 20, fitness: 15 },
    load_type: { solo: 5, solo_gear: 0, small_companion: -15 },
    total_load: { lt160: 5, '160_200': 0, '201_250': -10 },
    priority: { max_stability: -25, balanced: 0, speed: 25, maneuverable: 0 },
    experience: { never: -15, comfortable_wide: -5, regular: 10, performance: 20 },
  };

  function glideIndex(answers) {
    let index = 50;
    Object.keys(GLIDE_WEIGHTS).forEach((key) => {
      const delta = GLIDE_WEIGHTS[key][answers[key]];
      if (typeof delta === 'number') index += delta;
    });
    return index;
  }

  /**
   * Returns { archetype, glide_index }. glide_index is null whenever a
   * specialist override or the Captain trigger short-circuits the scoring (§8).
   */
  function resolveArchetype(answers, loadLb) {
    // Step 3 — specialist override.
    if (answers.use_case === 'fishing') {
      return { archetype: 'The Angler', glide_index: null };
    }
    if (answers.use_case === 'whitewater_surf') {
      return {
        archetype: answers.water === 'river_coast' ? 'The River Runner' : 'The Freestyler',
        glide_index: null,
      };
    }

    // Step 4 — Captain trigger. A physical constraint, not a preference, which
    // is why it sits ahead of any scoring.
    if (
      loadLb >= 251 ||
      answers.load_type === 'large_companion' ||
      answers.use_case === 'family_cargo' ||
      (answers.priority === 'max_stability' && loadLb >= 201)
    ) {
      return { archetype: 'The Tribe Captain', glide_index: null };
    }

    // Step 5 — glide index over the 4 remaining generalists.
    const index = glideIndex(answers);
    let archetype;
    if (index >= 75) archetype = 'The Performer';
    else if (index >= 55) archetype = 'The Voyager';
    // Explorer and Cruiser both sit at the bottom of the index — they are told
    // apart by the water, not by speed.
    else archetype = answers.water === 'calm_lake' ? 'The Cruiser' : 'The Explorer';

    return { archetype, glide_index: index };
  }

  /**
   * The archetype must describe a board we can actually hand over.
   *
   * Step 5 scores preference only; the step 2 filters (level, capacity) run
   * separately. When they disagree — a first-timer who wants speed scores
   * Performer, but every Performer board is tagged "Confident paddler" and up —
   * slot 1 falls back to an off-archetype board and the screen headlines
   * "You are a Performer" over a Sequoia. So the generalist archetype walks the
   * glide scale to the nearest one that has an eligible board, stability side
   * first at equal distance: too stable is a disappointment, too twitchy is a
   * swim. Specialists are left alone; their no_match is deliberate (§9).
   */
  function servableArchetype(archetype, pool, answers) {
    const index = GLIDE_SCALE.indexOf(archetype);
    if (index === -1) return archetype;
    const served = (name) =>
      pool.some((entry) => (entry.board.archetypes || []).indexOf(name) !== -1);
    if (served(archetype)) return archetype;

    for (let distance = 1; distance < GLIDE_SCALE.length; distance += 1) {
      const pick = [GLIDE_SCALE[index - distance], GLIDE_SCALE[index + distance]].find(
        (name) => name && served(name)
      );
      if (!pick) continue;
      // Cruiser and Explorer are told apart by the water, not by the index (step 5).
      if (pick === 'The Cruiser' || pick === 'The Explorer') {
        const byWater = answers.water === 'calm_lake' ? 'The Cruiser' : 'The Explorer';
        if (served(byWater)) return byWater;
      }
      return pick;
    }
    return archetype;
  }

  // --- Client activities ---------------------------------------------------

  // Signal strength per activity, used both for the fit bonus (§6) and for the
  // ordered `profile.activities` output (§8). Higher = stronger signal.
  const ACTIVITY_SIGNALS = [
    { activity: 'Fishing', weight: 10, when: (a) => a.use_case === 'fishing' },
    { activity: 'Whitewater', weight: 10, when: (a) => a.use_case === 'whitewater_surf' && a.water === 'river_coast' },
    { activity: 'Surf', weight: 10, when: (a) => a.use_case === 'whitewater_surf' && a.water !== 'river_coast' },
    { activity: 'Touring', weight: 9, when: (a) => a.use_case === 'distance' },
    { activity: 'Fitness & Yoga', weight: 9, when: (a) => a.use_case === 'fitness' },
    { activity: 'Kids & Family', weight: 8, when: (a) => a.use_case === 'family_cargo' || a.load_type === 'large_companion' },
    { activity: 'Camping & Cargo', weight: 7, when: (a) => a.use_case === 'family_cargo' || a.load_type === 'solo_gear' },
    { activity: 'Pets', weight: 6, when: (a) => a.load_type === 'small_companion' || a.load_type === 'large_companion' },
    { activity: 'Ocean & Coastal', weight: 6, when: (a) => a.water === 'river_coast' || a.water === 'big_lake_wind' },
    { activity: 'Cruising', weight: 5, when: (a) => a.use_case === 'relax' || a.water === 'calm_lake' },
    { activity: 'Kayak mode', weight: 4, when: (a) => hasInterest(a, 'kayak') },
  ];

  function hasInterest(answers, value) {
    return Array.isArray(answers.accessory_interests) && answers.accessory_interests.indexOf(value) !== -1;
  }

  function resolveActivities(answers) {
    const scored = [];
    ACTIVITY_SIGNALS.forEach((signal) => {
      if (!signal.when(answers)) return;
      if (scored.some((entry) => entry.activity === signal.activity)) return;
      scored.push({ activity: signal.activity, weight: signal.weight });
    });
    scored.sort((a, b) => b.weight - a.weight);
    return scored.slice(0, 4).map((entry) => entry.activity);
  }

  // --- Step 2: eliminating filters (§5) ------------------------------------

  function isEligible(board, level, loadLb, isAvailable) {
    // Fail CLOSED on an unparseable capacity. Dropping a board from the results
    // is recoverable; recommending one that cannot carry the paddler is not.
    if (!(board.capacity_lb > 0)) return false;
    if (loadLb > board.capacity_lb * CAPACITY_MARGIN) return false;
    if (!Array.isArray(board.levels) || board.levels.indexOf(level) === -1) return false;
    if (!isAvailable(board)) return false;
    return true;
  }

  // --- Fit score (§6) ------------------------------------------------------

  function fitScore(board, archetype, activities, loadLb) {
    let fit = 0;

    const boardArchetypes = Array.isArray(board.archetypes) ? board.archetypes : [];
    if (boardArchetypes[0] === archetype) fit += 40;
    else if (boardArchetypes.indexOf(archetype) > 0) fit += 20;

    const boardActivities = Array.isArray(board.activities) ? board.activities : [];
    const matches = activities.filter((activity) => boardActivities.indexOf(activity) !== -1).length;
    fit += Math.min(matches * 5, 20);

    const ratio = loadLb / board.capacity_lb;
    if (ratio >= 0.55 && ratio <= 0.85) fit += 15;
    else if (ratio >= 0.4) fit += 8;
    // ratio < 0.40: oversized but safe — no bonus, no penalty.

    fit += TIER_WEIGHT[board.price_tier] || 0;

    return fit;
  }

  /**
   * Tie-break cascade (§6): weekly sales desc, then price asc, then handle.
   *
   * Not cosmetic — it settles the real platform duplicates. Sequoia 11' (84/wk)
   * vs Maligne 11' (29/wk) have identical specs, same for Columbia 11' (37) vs
   * Athabasca 11' (32). We surface the proven winner and let the customer pick
   * their graphics afterwards.
   */
  function bestOf(pool) {
    if (!pool.length) return null;
    return pool.slice().sort((a, b) => {
      if (b.fit_score !== a.fit_score) return b.fit_score - a.fit_score;
      const salesA = a.board.avg_weekly_sales || 0;
      const salesB = b.board.avg_weekly_sales || 0;
      if (salesB !== salesA) return salesB - salesA;
      if (a.board.price_cad !== b.board.price_cad) return a.board.price_cad - b.board.price_cad;
      return String(a.board.handle).localeCompare(String(b.board.handle));
    })[0];
  }

  // "Platform" = same width + same capacity + same price. Two boards sharing a
  // platform are the same boat with different graphics — never show both.
  function platformKey(board) {
    return [board.width_in, board.capacity_lb, board.price_cad].join('|');
  }

  // --- Copy generation (§8) -------------------------------------------------

  // Every sentence the engine produces is a TOKEN — a stable key plus the
  // numbers that belong in it — never a concatenated string. `renderReason`
  // and `renderRationale` turn tokens into prose through a dictionary, and
  // the English dictionaries below are the engine's own.
  //
  // Cards therefore carry both: `reason_tokens` for a renderer holding a
  // French dictionary, and `reasons` already rendered through REASON_EN for
  // every caller that just wants the English. The prose is DERIVED from the
  // tokens, so there is still exactly one source of truth.
  //
  // Purity is untouched: this is string formatting over injected data — no
  // DOM, no network, no globals. A replay over archived answers still gets
  // byte-identical English out.

  const CLAUSE_EN = {
    'water.calm_lake': 'you paddle mostly on calm lakes and sheltered bays',
    'water.big_lake_wind': 'you paddle on big, sometimes choppy lakes',
    'water.river_coast': 'you paddle on rivers or along the coast',
    'water.anywhere': 'you paddle a bit of everywhere',
    'use_case.relax': 'you want to relax and explore close to shore',
    'use_case.distance': 'you want to cover more distance',
    'use_case.fitness': 'you want to train and improve your technique',
    'use_case.family_cargo': 'you bring a passenger or a lot of gear along',
    'use_case.fishing': 'you are on the water to fish',
    'use_case.whitewater_surf': 'you are after whitewater and surf',
    'experience.never': 'you are just getting started',
    'experience.comfortable_wide': 'you are already comfortable on a wide board',
    'experience.regular': 'you paddle regularly and have solid balance',
    'experience.performance': 'you want a demanding performance board',
  };

  // `{against}` is a product name and is never translated. `{archetype}` and
  // `{activities}` are resolved through the dictionary's own `archetype_profile`
  // and `activity` maps, which is what lets French say "profil d'explorateur"
  // where English says "Explorer profile" — the preposition lives in the French
  // dictionary entry, not in the template.
  const REASON_EN = {
    alt_more_stable: 'More stable underfoot than the {against}',
    alt_quicker: 'Quicker over distance than the {against}',
    budget_same_for_less: 'The same kind of paddling as the {against}, for less',
    archetype_exact: 'Built exactly for your {archetype} profile',
    archetype_also: 'Also suits your {archetype} profile',
    load_margin: 'Carries your {load} lb with plenty of margin ({capacity} lb rated)',
    load_comfortable: 'Rated for {capacity} lb — comfortable for your {load} lb load',
    load_max: 'Rated for {capacity} lb, the most capacity in this lineup',
    tracks_chop: 'Tracks well through wind and chop',
    wide_stability: '{width}" wide for maximum stability underfoot',
    touring_glide: 'Touring hull shape for more glide per stroke',
    covers_activities: 'Covers what you told us you want to do: {activities}',
    specs: '{width}" wide, {capacity} lb capacity',
    bestseller: 'One of our best-selling boards this season',
    load_warning: 'Close to this board’s recommended capacity for your total load.',
  };

  /** "The Explorer" -> "Explorer". English only; French supplies its own form. */
  function archetypeProfileEn(archetype) {
    return String(archetype || '').replace(/^The /, '');
  }

  function joinActivities(list, dictionary) {
    return (Array.isArray(list) ? list : [])
      .map((activity) => (dictionary && dictionary[activity]) || activity)
      .join(', ')
      .toLowerCase();
  }

  /**
   * Token -> sentence. `dictionary` is optional and partial; anything it does
   * not answer falls back to English, so a half-translated payload degrades a
   * sentence at a time instead of blanking the card.
   */
  function renderReason(token, dictionary) {
    if (!token || !token.key) return '';
    const dict = dictionary || {};
    const template = (dict.reason && dict.reason[token.key]) || REASON_EN[token.key];
    if (!template) return '';
    const vars = token.vars || {};
    return template.replace(/\{(\w+)\}/g, function (match, name) {
      if (name === 'archetype') {
        return (
          (dict.archetype_profile && dict.archetype_profile[vars.archetype]) ||
          archetypeProfileEn(vars.archetype)
        );
      }
      if (name === 'activities') return joinActivities(vars.activities, dict.activity);
      return vars[name] == null ? '' : String(vars[name]);
    });
  }

  /** Rationale tokens are bare keys into CLAUSE_EN — there is nothing to fill. */
  function buildRationaleTokens(answers) {
    return ['water.' + answers.water, 'use_case.' + answers.use_case, 'experience.' + answers.experience].filter(
      (key) => CLAUSE_EN[key]
    );
  }

  function renderRationale(tokens, dictionary) {
    const clauses = (dictionary && dictionary.clause) || {};
    const parts = (Array.isArray(tokens) ? tokens : [])
      .map((key) => clauses[key] || CLAUSE_EN[key])
      .filter(Boolean);
    if (!parts.length) return '';
    const sentence = parts.join(', ');
    return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
  }

  /** Exactly 3 reason tokens, built from the actual answers (§8). */
  function buildReasonTokens(board, answers, archetype, activities, loadLb, context) {
    const candidates = [];
    const boardActivities = Array.isArray(board.activities) ? board.activities : [];
    const boardArchetypes = Array.isArray(board.archetypes) ? board.archetypes : [];
    const against = context && context.comparedTo;
    const push = (key, vars) => candidates.push({ key: key, vars: vars || {} });

    // A slot-2 or slot-3 card has to justify ITS OWN role, not restate why the
    // best choice was picked — otherwise the alternative reads as a duplicate.
    if (context && context.slot === 'alternative' && against) {
      push(context.label === SLOT_LABELS.more_stable ? 'alt_more_stable' : 'alt_quicker', { against: against });
    }
    if (context && context.slot === 'budget' && against) {
      push('budget_same_for_less', { against: against });
    }

    if (boardArchetypes[0] === archetype) {
      push('archetype_exact', { archetype: archetype });
    } else if (boardArchetypes.indexOf(archetype) !== -1) {
      push('archetype_also', { archetype: archetype });
    }

    const ratio = loadLb / board.capacity_lb;
    if (ratio <= 0.6) {
      push('load_margin', { load: loadLb, capacity: board.capacity_lb });
    } else if (ratio <= LOAD_WARNING_RATIO) {
      push('load_comfortable', { load: loadLb, capacity: board.capacity_lb });
    } else {
      push('load_max', { capacity: board.capacity_lb });
    }

    if (answers.water === 'big_lake_wind' || answers.water === 'river_coast') {
      if (boardActivities.indexOf('Ocean & Coastal') !== -1) push('tracks_chop');
    }
    if (answers.priority === 'max_stability' && board.width_in >= 33) {
      push('wide_stability', { width: board.width_in });
    }
    if (answers.priority === 'speed' && boardActivities.indexOf('Touring') !== -1) {
      push('touring_glide');
    }

    const matched = activities.filter((activity) => boardActivities.indexOf(activity) !== -1);
    if (matched.length) {
      push('covers_activities', { activities: matched.slice(0, 3) });
    }
    if (board.width_in) push('specs', { width: board.width_in, capacity: board.capacity_lb });
    push('bestseller');

    const tokens = [];
    const seen = {};
    candidates.forEach((token) => {
      const identity = token.key + '|' + JSON.stringify(token.vars);
      if (tokens.length < 3 && !seen[identity]) {
        seen[identity] = true;
        tokens.push(token);
      }
    });
    return tokens;
  }

  function buildCard(entry, slot, label, answers, archetype, activities, loadLb, comparedTo) {
    const board = entry.board;
    const ratio = loadLb / board.capacity_lb;
    const overCapacity = ratio > LOAD_WARNING_RATIO;
    const tokens = buildReasonTokens(board, answers, archetype, activities, loadLb, {
      slot: slot,
      label: label,
      comparedTo: comparedTo || null,
    });
    return {
      slot: slot,
      label: label,
      product_id: String(board.product_id == null ? '' : board.product_id),
      handle: board.handle,
      short_name: board.short_name || board.title || board.handle,
      price_cad: board.price_cad,
      price_tier: board.price_tier,
      fit_score: entry.fit_score,
      reason_tokens: tokens,
      // Derived from `reason_tokens` through the engine's own English
      // dictionary, so a caller with no dictionary still gets prose.
      reasons: tokens.map((token) => renderReason(token, null)),
      load_warning: overCapacity ? renderReason({ key: 'load_warning' }, null) : null,
      // The key, so a renderer with a French dictionary can say it in French.
      load_warning_key: overCapacity ? 'load_warning' : null,
      // Passed through untouched so the UI can render without a second lookup.
      url: board.url || null,
      image: board.image || null,
      variant_id: board.variant_id || null,
      title: board.title || null,
    };
  }

  // --- Accessories (§7) ----------------------------------------------------

  // Observed attach rates, used to order everything that was NOT explicitly
  // asked for via accessory_interests.
  const ATTACH_RATE = {
    'bottle-holder-for-paddle-board-screw-in-mounting': 17.9,
    '20psi-electric-pump': 9.5,
    'waterproof-phone-case': 9.3,
    'tofino-dry-bag': 7.0,
    'sup-foldable-anker': 6.2,
    'sup-insulated-cooler': 6.2,
    'kayak-seat-for-paddleboards': 5.7,
    'sup-to-kayak-paddle': 5.7,
    'sup-shoulder-strap-leash': 3.0,
    'official-vfi': 3.0,
    'sup-rod-holder': 3.0,
  };

  // Signal -> accessory rules (§7). `declared` marks rules driven by the
  // customer's own accessory_interests answer, which always rank first.
  const ACCESSORY_RULES = [
    {
      handles: ['20psi-electric-pump'],
      reason: 'Inflate to 22 PSI without wearing yourself out before you paddle',
      when: (a, board) => hasInterest(a, 'easy_inflation') || (board && board.width_in >= 33),
      declared: (a) => hasInterest(a, 'easy_inflation'),
    },
    {
      handles: ['waterproof-phone-case'],
      reason: 'Keep your phone reachable and dry',
      when: (a) => hasInterest(a, 'phone_dry'),
      declared: () => true,
    },
    {
      handles: ['sup-insulated-cooler'],
      reason: 'Bring cold drinks and lunch along',
      when: (a) => hasInterest(a, 'food_drinks'),
      declared: () => true,
    },
    {
      handles: ['tofino-dry-bag'],
      reason: 'A 15 L dry bag for the gear you bring for everyone',
      when: (a) => hasInterest(a, 'food_drinks') && a.use_case === 'family_cargo',
      declared: () => true,
    },
    {
      handles: ['bottle-holder-for-paddle-board-screw-in-mounting'],
      reason: 'Keep your drink within reach while you cruise',
      when: (a) => a.use_case === 'relax' || a.water === 'calm_lake',
      declared: () => false,
    },
    {
      // Seat and paddle are always recommended together — one without the
      // other does not turn the board into a kayak.
      handles: ['kayak-seat-for-paddleboards', 'sup-to-kayak-paddle'],
      reason: 'Turn your board into a kayak for long seated outings',
      when: (a) => hasInterest(a, 'kayak'),
      declared: () => true,
    },
    {
      handles: ['sup-shoulder-strap-leash'],
      reason: 'Carry the board from the car to the water one-handed',
      when: (a) => hasInterest(a, 'transport'),
      declared: () => true,
    },
    {
      handles: ['sup-foldable-anker'],
      reason: 'Hold your spot instead of drifting with the wind',
      when: (a) => hasInterest(a, 'stay_put') || a.use_case === 'fishing',
      declared: (a) => hasInterest(a, 'stay_put'),
    },
    {
      handles: ['official-vfi'],
      reason: 'Paddle safely — pick your size before adding',
      when: (a) => hasInterest(a, 'safety'),
      declared: () => true,
    },
    {
      // TODO: confirm the live handle for the rod holder. Unknown handles are
      // skipped silently because they never resolve in the injected catalog.
      handles: ['sup-foldable-anker', 'sup-rod-holder', 'sup-insulated-cooler'],
      reason: 'The fishing set-up: anchor, rod holder and cooler',
      when: (a) => a.use_case === 'fishing',
      declared: () => false,
    },
  ];

  const BUNDLE_HANDLES = [
    '20psi-electric-pump',
    'bottle-holder-for-paddle-board-screw-in-mounting',
    'waterproof-phone-case',
    'sup-insulated-cooler',
  ];

  const MAX_PERSONALISED_ACCESSORIES = 3;

  /**
   * §7 rule 1 says to read `custom.included_with_product` and never recommend
   * something the board already ships with. In the live catalog that metafield
   * is a single free-text blob ("...Double action pump\nAnkle strap\nRepair
   * kit"), not a list of product references, so a handle comparison never
   * matches. These phrases are the text fallback.
   *
   * They must be PRECISE. "pump" alone would match the "Double action pump"
   * that every board includes and would suppress the electric pump upsell
   * everywhere — the second-highest attach rate we have.
   */
  const INCLUDED_PHRASES = {
    '20psi-electric-pump': ['electric pump'],
    'sup-foldable-anker': ['anchor', 'anker'],
    'sup-rod-holder': ['rod holder'],
    'sup-to-kayak-paddle': ['kayak paddle'],
    'kayak-seat-for-paddleboards': ['kayak seat'],
    'sup-shoulder-strap-leash': ['shoulder strap'],
    'official-vfi': ['flotation jacket', 'pfd'],
    'sup-insulated-cooler': ['cooler'],
    'waterproof-phone-case': ['phone case'],
    'tofino-dry-bag': ['dry bag'],
    'bottle-holder-for-paddle-board-screw-in-mounting': ['bottle holder'],
  };

  function isIncludedWithBoard(handle, included) {
    if (!included) return false;
    const entries = Array.isArray(included) ? included : [included];
    // Product-reference list: exact handle match.
    if (entries.indexOf(handle) !== -1) return true;
    // Free-text list: precise phrase match.
    const phrases = INCLUDED_PHRASES[handle];
    if (!phrases) return false;
    const haystack = entries
      .filter((entry) => typeof entry === 'string')
      .join('\n')
      .toLowerCase();
    if (!haystack) return false;
    return phrases.some((phrase) => haystack.indexOf(phrase) !== -1);
  }

  function buildAccessories(answers, board, inventory) {
    const catalog = (inventory && inventory.accessories) || {};
    const cartHandles = (inventory && inventory.cartHandles) || [];
    // Rule 1: never recommend something already bundled with the board. Every
    // board already ships with a carbon paddle, a wheeled bag and a hand pump.
    const included = (board && board.included_with_product) || [];

    function isOffered(handle) {
      const product = catalog[handle];
      if (!product || product.available === false) return false;
      if (isIncludedWithBoard(handle, included)) return false;
      if (cartHandles.indexOf(handle) !== -1) return false;
      return true;
    }

    const candidates = [];
    ACCESSORY_RULES.forEach((rule) => {
      if (!rule.when(answers, board)) return;
      const declared = rule.declared(answers, board);
      rule.handles.forEach((handle) => {
        if (!isOffered(handle)) return;
        if (candidates.some((candidate) => candidate.handle === handle)) return;
        candidates.push({
          handle: handle,
          reason: rule.reason,
          rank: declared ? 0 : 1,
          attach_rate: ATTACH_RATE[handle] || 0,
        });
      });
    });

    // Rule 3: declared intent first, then observed attach rate.
    candidates.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return b.attach_rate - a.attach_rate;
    });

    const picks = candidates.slice(0, MAX_PERSONALISED_ACCESSORIES);

    // Rule 4: after the personalised picks, show the universal bundle.
    BUNDLE_HANDLES.forEach((handle) => {
      if (!isOffered(handle)) return;
      if (picks.some((pick) => pick.handle === handle)) return;
      picks.push({
        handle: handle,
        reason: 'Part of our essentials bundle',
        rank: 2,
        attach_rate: ATTACH_RATE[handle] || 0,
        bundle: true,
      });
    });

    return picks.map((pick) => {
      const product = catalog[pick.handle] || null;
      // Derived from the catalog, never from a per-rule flag. A hardcoded flag
      // is a second source of truth that goes stale: it named the PFD and
      // missed the Tofino dry bag, whose six colours were being chosen for the
      // customer by whichever variant happened to be first.
      const options = (product && product.variant_options) || [];
      const variantCount = (product && product.variant_count) || 1;
      return {
        handle: pick.handle,
        reason: pick.reason,
        requires_variant: options.length > 0 || variantCount > 1,
        // The UI labels the button with this rather than assuming "size".
        variant_options: options,
        bundle: Boolean(pick.bundle),
        // Passed through so the UI does not need a second lookup.
        product: product,
      };
    });
  }

  // --- Public entry point --------------------------------------------------

  /**
   * @param {Object} answers   see the Answers contract (§4)
   * @param {Array}  boards    board catalog (metafields + price + specs)
   * @param {Object} inventory { available?, accessories?, cartHandles? }
   * @returns {Object} Result (§8)
   */
  function recommend(answers, boards, inventory) {
    const safeAnswers = answers || {};
    // Shallow-copied, never mutated in place, so archived board snapshots stay
    // byte-identical across a replay.
    const catalog = (Array.isArray(boards) ? boards : []).map(normalizeBoard);
    const inv = inventory || {};
    const availability = inv.available || null;

    function isAvailable(board) {
      if (availability && Object.prototype.hasOwnProperty.call(availability, board.handle)) {
        return Boolean(availability[board.handle]);
      }
      return board.available !== false;
    }

    const loadLb = LOAD_LB[safeAnswers.total_load] || 0;
    const level = resolveLevel(safeAnswers);
    const resolved = resolveArchetype(safeAnswers, loadLb);
    const activities = resolveActivities(safeAnswers);
    const rationaleTokens = buildRationaleTokens(safeAnswers);

    const profile = {
      archetype: resolved.archetype,
      level: level,
      activities: activities,
      glide_index: resolved.glide_index,
      // The archetype the answers scored before servableArchetype moved it, or
      // null. Kept so a replay can tell a preference from a catalog gap.
      adjusted_from: null,
      headline: (ARCHETYPE_COPY[resolved.archetype] || {}).headline || '',
      rationale_tokens: rationaleTokens,
      rationale: renderRationale(rationaleTokens, null),
    };

    // Over 300 lb always needs a human to confirm capacity (§5), whatever else
    // the answers say. The profile is still returned so the screen can be
    // personalised while the specialist takes over.
    if (safeAnswers.total_load === 'gt300') {
      return { status: 'manual_review', profile: profile, boards: [], accessories: [] };
    }

    const eligible = catalog
      .filter((board) => isEligible(board, level, loadLb, isAvailable))
      .map((board) => ({ board: board, fit_score: 0 }));

    // Never loosen the filters to manufacture a result (§5).
    if (!eligible.length) {
      return { status: 'no_match', profile: profile, boards: [], accessories: [] };
    }

    const nonBudget = eligible.filter((entry) => entry.board.price_tier !== 'budget');

    // Slot 1 draws from the non-budget pool whenever it has anything, so that
    // is the pool the archetype has to be servable from.
    const archetype = servableArchetype(resolved.archetype, nonBudget.length ? nonBudget : eligible, safeAnswers);
    if (archetype !== resolved.archetype) {
      profile.adjusted_from = resolved.archetype;
      profile.archetype = archetype;
      profile.headline = (ARCHETYPE_COPY[archetype] || {}).headline || '';
      resolved.archetype = archetype;
    }
    eligible.forEach((entry) => {
      entry.fit_score = fitScore(entry.board, resolved.archetype, activities, loadLb);
    });

    // --- Slot 1: best choice. A budget board only reaches slot 1 if nothing
    // else clears the filters at all. This is the rule that keeps the Nomad
    // out of the top card.
    const onArchetype = nonBudget.filter(
      (entry) => (entry.board.archetypes || []).indexOf(resolved.archetype) !== -1
    );

    const isSpecialist = GLIDE_SCALE.indexOf(resolved.archetype) === -1;

    // The §6 fallback cascade (any archetype, then budget included) only makes
    // sense for a generalist: a Cruiser handed an Explorer is still a usable
    // board. A specialist has no such fallback — the whole point of the §5
    // override is that only that specialist board serves the need, so handing a
    // whitewater paddler a cruising board is worse than handing them to a human.
    // This is what §9 means by "beginner + whitewater_surf -> no_match".
    if (isSpecialist && !onArchetype.length) {
      return { status: 'no_match', profile: profile, boards: [], accessories: [] };
    }

    const slot1 = bestOf(onArchetype) || bestOf(nonBudget) || bestOf(eligible);

    const cards = [
      buildCard(slot1, 'best', SLOT_LABELS.best, safeAnswers, resolved.archetype, activities, loadLb),
    ];

    // --- Slot 2: must solve a DIFFERENT trade-off, not offer another colourway.
    const slot1Index = GLIDE_SCALE.indexOf(resolved.archetype);
    let targets;
    if (isSpecialist) {
      const fixed = SPECIALIST_ADJACENCY[resolved.archetype];
      targets = fixed ? [fixed] : [];
    } else {
      targets = [GLIDE_SCALE[slot1Index - 1], GLIDE_SCALE[slot1Index + 1]].filter(Boolean);
    }

    const slot1Platform = platformKey(slot1.board);
    const slot2Pool = nonBudget.filter((entry) => {
      if (platformKey(entry.board) === slot1Platform) return false;
      return (entry.board.archetypes || []).some((archetype) => targets.indexOf(archetype) !== -1);
    });
    const slot2 = bestOf(slot2Pool);

    if (slot2) {
      const slot2Archetype = (slot2.board.archetypes || []).find(
        (archetype) => targets.indexOf(archetype) !== -1
      );
      const slot2Index = GLIDE_SCALE.indexOf(slot2Archetype);
      const isMoreStable = slot1Index === -1 || (slot2Index !== -1 && slot2Index < slot1Index);
      cards.push(
        buildCard(
          slot2,
          'alternative',
          isMoreStable ? SLOT_LABELS.more_stable : SLOT_LABELS.faster,
          safeAnswers,
          resolved.archetype,
          activities,
          loadLb,
          cards[0].short_name
        )
      );
    }

    // --- Slot 3: budget option. Always third, never first.
    const budgetPool = eligible.filter(
      (entry) => entry.board.price_tier === 'budget' && entry.board.price_cad < slot1.board.price_cad
    );
    const slot3 = bestOf(budgetPool);
    if (slot3) {
      cards.push(
        buildCard(
          slot3,
          'budget',
          SLOT_LABELS.budget,
          safeAnswers,
          resolved.archetype,
          activities,
          loadLb,
          cards[0].short_name
        )
      );
    }

    return {
      status: 'ok',
      profile: profile,
      boards: cards,
      accessories: buildAccessories(safeAnswers, slot1.board, inv),
    };
  }

  root.TwtQuizRecommend = {
    recommend: recommend,
    // Exposed for tests, tuning and replay tooling.
    ENGINE_VERSION: ENGINE_VERSION,
    encodeAnswers: encodeAnswers,
    decodeAnswers: decodeAnswers,
    ANSWER_VALUES: ANSWER_VALUES,
    ANSWER_KEYS: ANSWER_KEYS,
    TIER_WEIGHT: TIER_WEIGHT,
    derivePriceTier: derivePriceTier,
    PRICE_TIER_BY_HANDLE: PRICE_TIER_BY_HANDLE,
    normalizeBoard: normalizeBoard,
    parseMeasure: parseMeasure,
    parseLength: parseLength,
    isIncludedWithBoard: isIncludedWithBoard,
    LOAD_LB: LOAD_LB,
    CAPACITY_MARGIN: CAPACITY_MARGIN,
    ARCHETYPES: ARCHETYPES,
    LEVELS: LEVELS,
    ACTIVITIES: ACTIVITIES,
    GLIDE_SCALE: GLIDE_SCALE,
    SPECIALIST_ADJACENCY: SPECIALIST_ADJACENCY,
    SLOT_LABELS: SLOT_LABELS,
    ARCHETYPE_COPY: ARCHETYPE_COPY,
    BUNDLE_HANDLES: BUNDLE_HANDLES,
    resolveLevel: resolveLevel,
    resolveArchetype: resolveArchetype,
    servableArchetype: servableArchetype,
    resolveActivities: resolveActivities,
    glideIndex: glideIndex,
    fitScore: fitScore,
    platformKey: platformKey,
    // Copy generation (§8). Exposed so the rendering layer can re-render the
    // same tokens through a translated dictionary.
    REASON_EN: REASON_EN,
    CLAUSE_EN: CLAUSE_EN,
    renderReason: renderReason,
    renderRationale: renderRationale,
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
